import { EventTrigger } from "./EventTrigger.js";
import { calendarDate, calendarMonth, groupSegmentsByDay, isSameDay, splitIntoDaySegments } from "./utils/date-grid.js";
import { useMemo, useState } from "react";
import { Button, Dropdown } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
//#region src/components/Calendar/MonthGrid.tsx
var MAX_CHIPS = 3;
var WEEKDAYS = [
	"Sun",
	"Mon",
	"Tue",
	"Wed",
	"Thu",
	"Fri",
	"Sat"
];
function MonthGrid(props) {
	const segmentsByDay = useMemo(() => groupSegmentsByDay(props.events.flatMap((event) => splitIntoDaySegments(event, props.days, props.timeZone))), [
		props.days,
		props.events,
		props.timeZone
	]);
	const focusMonth = calendarMonth(props.anchorMs, props.timeZone);
	const [now] = useState(() => Date.now());
	return /* @__PURE__ */ jsx("div", {
		className: "border-border overflow-hidden rounded border",
		children: /* @__PURE__ */ jsxs("div", {
			className: "grid grid-cols-7",
			children: [WEEKDAYS.map((label) => /* @__PURE__ */ jsx("div", {
				className: "text-color-subtle py-xxs text-center text-sm",
				children: label
			}, label)), props.days.map((day) => /* @__PURE__ */ jsx(DayCell, {
				day,
				dayClassName: props.dayClassName,
				inMonth: calendarMonth(day, props.timeZone) === focusMonth,
				isToday: isSameDay(day, now, props.timeZone),
				onDayClick: props.onDayClick,
				onEventClick: props.onEventClick,
				renderDayContent: props.renderDayContent,
				renderEventContent: props.renderEventContent,
				renderEventDetail: props.renderEventDetail,
				segments: segmentsByDay.get(day) ?? [],
				timeZone: props.timeZone
			}, day))]
		})
	});
}
function DayCell(props) {
	const wash = props.dayClassName?.(props.day, props.segments);
	const clickable = Boolean(props.onDayClick && props.segments.length > 0);
	const useCustomDay = Boolean(props.renderDayContent);
	const body = useCustomDay ? props.renderDayContent?.(props.day, props.segments) : /* @__PURE__ */ jsx(ChipList, {
		onEventClick: props.onEventClick,
		renderEventContent: props.renderEventContent,
		renderEventDetail: props.renderEventDetail,
		segments: props.segments
	});
	const className = classnames("border-border min-h-[96px] border-t border-l p-xxs", !props.inMonth && !wash && "bg-surface-subtle", props.inMonth && !wash && "bg-transparent", wash, props.isToday && "border-main border", clickable && "cursor-pointer");
	const dateClassName = classnames("text-right text-sm", props.isToday ? "text-color-strong font-semibold" : props.inMonth ? "text-color-subtle" : "text-color-subtle opacity-60");
	const dateNumber = calendarDate(props.day, props.timeZone);
	if (useCustomDay && clickable) return /* @__PURE__ */ jsxs("button", {
		className: `${className} w-full text-left`,
		onClick: () => props.onDayClick?.(props.day),
		type: "button",
		children: [/* @__PURE__ */ jsx("div", {
			className: dateClassName,
			children: dateNumber
		}), body]
	});
	return /* @__PURE__ */ jsxs("div", {
		className,
		onClick: clickable && !useCustomDay ? () => props.onDayClick?.(props.day) : void 0,
		children: [/* @__PURE__ */ jsx("div", {
			className: dateClassName,
			children: dateNumber
		}), body]
	});
}
function ChipList(props) {
	const visible = props.segments.slice(0, MAX_CHIPS);
	const overflow = props.segments.length - visible.length;
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-px",
		children: [visible.map((seg) => /* @__PURE__ */ jsx(EventTrigger, {
			className: "block w-full truncate rounded border px-xxs text-left text-sm",
			dropdownPlace: "bottom",
			event: seg.event,
			onEventClick: props.onEventClick,
			renderEventContent: props.renderEventContent,
			renderEventDetail: props.renderEventDetail,
			segment: {
				clippedEnd: seg.clippedEnd,
				clippedStart: seg.clippedStart
			}
		}, `${seg.event.key}:${seg.dayStartMs}`)), overflow > 0 && /* @__PURE__ */ jsx(Dropdown, {
			className: "block min-w-full",
			controller: ({ open }) => /* @__PURE__ */ jsxs(Button, {
				fullWidth: true,
				onClick: (e) => {
					e.stopPropagation();
					open();
				},
				size: "sm",
				subtle: true,
				children: [
					"+",
					overflow,
					" more"
				]
			}),
			dropdownPlace: "bottom",
			children: /* @__PURE__ */ jsx("div", {
				className: "max-h-[320px] space-y-xxs overflow-y-auto p-xxs",
				children: props.segments.map((seg) => /* @__PURE__ */ jsx("div", {
					className: "border-border border-b pb-xxs last:border-b-0",
					children: props.renderEventDetail?.(seg.event)
				}, `${seg.event.key}:${seg.dayStartMs}`))
			})
		})]
	});
}
//#endregion
export { MonthGrid };

//# sourceMappingURL=MonthGrid.js.map